import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import { HashLink } from "react-router-hash-link";
import { motion, useReducedMotion } from "framer-motion";
import Logo from "../../stuff/without.jpeg";

const PRIMARY = "#0c513f";
const ACCENT = "#ffbf07";

const Section1 = () => {
  const reduceMotion = useReducedMotion();

  const floatAnim = reduceMotion
    ? {}
    : { y: [0, -10, 0], x: [0, 6, 0], opacity: [0.55, 0.8, 0.55] };

  const sheenAnim = reduceMotion ? {} : { x: ["-20%", "140%"] };

  return (
    <section
      id="Home"
      style={{
        position: "relative",
        overflow: "hidden",
        background: PRIMARY,
        paddingTop: "28px",
        paddingBottom: "80px",
      }}
    >
      {/* Clean premium background (simple + classy) */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(900px 520px at 20% 18%, rgba(255,191,7,0.18), transparent 60%)," +
            "radial-gradient(900px 520px at 80% 30%, rgba(255,255,255,0.10), transparent 55%)," +
            "linear-gradient(120deg, #0c513f, #0b4a3a, #0c513f)",
        }}
      />

      {/* Subtle floating glow blob */}
      <motion.div
        aria-hidden="true"
        animate={floatAnim}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
        style={{
          position: "absolute",
          top: "-120px",
          right: "-140px",
          width: 460,
          height: 460,
          borderRadius: 999,
          background: "rgba(255,191,7,0.16)",
          filter: "blur(60px)",
          transform: "translateZ(0)",
          pointerEvents: "none",
        }}
      />

      <Container style={{ position: "relative", zIndex: 2 }}>
        {/* Logo (simple) */}
        <Row className="justify-content-end">
          <Col xs="auto">
            <motion.img
              src={Logo}
              alt="Logo"
              initial={reduceMotion ? false : { opacity: 0, y: -8, scale: 0.98 }}
              animate={reduceMotion ? {} : { opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              style={{
                maxWidth: 140,
                width: "28vw",
                minWidth: 86,
                margin: "18px 6px 0 0",
                borderRadius: 16,
                border: "1px solid rgba(255,255,255,0.18)",
                boxShadow: "0 16px 40px rgba(0,0,0,0.22)",
              }}
            />
          </Col>
        </Row>

        {/* Hero */}
        <Row className="justify-content-center">
          <Col lg={9} className="text-center">
            {/* Badge */}
            <motion.div
              initial={reduceMotion ? false : { opacity: 0, y: 10 }}
              animate={reduceMotion ? {} : { opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 14px",
                borderRadius: 999,
                background: "rgba(255,255,255,0.12)",
                border: "1px solid rgba(255,255,255,0.18)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                marginTop: 14,
                marginBottom: 18,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 999,
                  background: ACCENT,
                  boxShadow: "0 0 18px rgba(255,191,7,0.7)",
                }}
              />
            
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={reduceMotion ? false : { opacity: 0, y: 16 }}
              animate={reduceMotion ? {} : { opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              style={{
                color: "white",
                fontSize: "clamp(36px, 4.6vw, 56px)",
                lineHeight: 1.05,
                letterSpacing: "-0.6px",
                marginBottom: 14,
                textShadow: "0 18px 60px rgba(0,0,0,0.28)",
              }}
            >
              Your{" "}
              <span
                style={{
                  background: `linear-gradient(90deg, ${ACCENT}, #ffffff)`,
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                Food Hub
              </span>{" "}
              <br />
              made ridiculously easy.
            </motion.h1>

            {/* Glass card (clean, not too much) */}
            <motion.div
              initial={reduceMotion ? false : { opacity: 0, y: 18 }}
              animate={reduceMotion ? {} : { opacity: 1, y: 0 }}
              transition={{ duration: 0.85, ease: "easeOut", delay: 0.05 }}
              style={{
                maxWidth: 860,
                margin: "0 auto",
                padding: "18px 18px",
                borderRadius: 22,
                background: "rgba(255,255,255,0.10)",
                border: "1px solid rgba(255,255,255,0.16)",
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
                boxShadow: "0 22px 70px rgba(0,0,0,0.20)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* gentle sheen */}
              <motion.div
                aria-hidden="true"
                animate={sheenAnim}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                style={{
                  position: "absolute",
                  top: "-55%",
                  left: "-30%",
                  width: "55%",
                  height: "220%",
                  background: "rgba(255,255,255,0.12)",
                  transform: "rotate(18deg)",
                  filter: "blur(2px)",
                }}
              />

              <p
                style={{
                  margin: 0,
                  fontSize: "clamp(15px, 1.25vw, 18px)",
                  lineHeight: 1.7,
                  color: "rgba(255,255,255,0.92)",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                Discover great meals, order in seconds, and track your delivery smoothly —
                from kitchen to doorstep.
                <br />
                <br />
                Add notes like “no pepper”, schedule delivery, and keep everything stress-free.
                Vendors and riders also get a clean flow to earn.
              </p>

              {/* Simple pills */}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 10,
                  justifyContent: "center",
                  marginTop: 14,
                  position: "relative",
                  zIndex: 1,
                }}
              >
                {["Schedule delivery", "Custom notes", "Live updates"].map((label) => (
                  <span
                    key={label}
                    style={{
                      padding: "9px 12px",
                      borderRadius: 999,
                      background: "rgba(253,247,233,0.10)",
                      border: "1px solid rgba(255,255,255,0.14)",
                      color: "rgba(255,255,255,0.90)",
                      fontSize: 13.5,
                    }}
                  >
                    {label}
                  </span>
                ))}
              </div>
            </motion.div>

            {/* CTA row */}
            <motion.div
              initial={reduceMotion ? false : { opacity: 0, y: 16 }}
              animate={reduceMotion ? {} : { opacity: 1, y: 0 }}
              transition={{ duration: 0.85, ease: "easeOut", delay: 0.12 }}
              style={{
                marginTop: 26,
                display: "flex",
                justifyContent: "center",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <HashLink
                smooth
                to="#download"
                style={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "14px 20px",
                  borderRadius: 999,
                  textDecoration: "none",
                  color: PRIMARY,
                  background: `linear-gradient(135deg, ${ACCENT}, #ffd45b)`,
                  border: "1px solid rgba(255,255,255,0.22)",
                  boxShadow: "0 22px 70px rgba(0,0,0,0.22)",
                  fontWeight: 800,
                  letterSpacing: "0.2px",
                }}
              >
                Download Now
                <span
                  aria-hidden="true"
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 999,
                    display: "grid",
                    placeItems: "center",
                    background: "rgba(12,81,63,0.14)",
                    border: "1px solid rgba(12,81,63,0.20)",
                    fontWeight: 900,
                  }}
                >
                  ➜
                </span>
              </HashLink>

              <HashLink
                smooth
                to="#features"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "14px 18px",
                  borderRadius: 999,
                  textDecoration: "none",
                  color: "rgba(255,255,255,0.92)",
                  background: "rgba(255,255,255,0.10)",
                  border: "1px solid rgba(255,255,255,0.16)",
                  backdropFilter: "blur(10px)",
                  WebkitBackdropFilter: "blur(10px)",
                  fontWeight: 700,
                }}
              >
                See Features
              </HashLink>
            </motion.div>

            {/* tiny note */}
            <div style={{ marginTop: 14, color: "rgba(255,255,255,0.70)", fontSize: 13 }}>
              Smooth ordering. Clean delivery. Zero stress.
            </div>
          </Col>
        </Row>
      </Container>

      {/* bottom fade */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: 90,
          background: "linear-gradient(180deg, rgba(12,81,63,0), rgba(12,81,63,0.65), rgba(12,81,63,1))",
          pointerEvents: "none",
        }}
      />
    </section>
  );
};

export default Section1;
